import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Fab,
  BottomNavigation,
  BottomNavigationAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
  ThemeProvider,
  CssBaseline,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Home as HomeIcon,
  Notes as NotesIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { AppProvider, useApp } from './contexts/AppContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { Note } from './types';
import { lightTheme } from './themes';

const MainApp: React.FC = () => {
  const { state, addNote, updateNote, deleteNote } = useApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentView, setCurrentView] = useState<'home' | 'notes' | 'search'>('home');
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const handleNewNote = useCallback(() => {
    setEditingNote(null);
    setNoteTitle('');
    setNoteContent('');
    setShowNoteDialog(true);
  }, []);

  const handleEditNote = useCallback((note: Note) => {
    setEditingNote(note);
    setNoteTitle(note.title);
    setNoteContent(note.content);
    setShowNoteDialog(true);
  }, []);

  const handleSaveNote = useCallback(() => {
    if (!noteTitle.trim()) return;

    const now = new Date();
    
    if (editingNote) {
      updateNote({
        ...editingNote,
        title: noteTitle.trim(),
        content: noteContent.trim(),
        updatedAt: now,
        wordCount: noteContent.trim().length
      });
    } else {
      addNote({
        title: noteTitle.trim(),
        content: noteContent.trim(),
        folderId: 'default',
        tags: [],
        wordCount: noteContent.trim().length
      });
    }

    setShowNoteDialog(false);
    setEditingNote(null);
    setNoteTitle('');
    setNoteContent('');
  }, [noteTitle, noteContent, editingNote, addNote, updateNote]);

  const handleDeleteNote = useCallback((noteId: string) => {
    if (window.confirm('确定要删除这条笔记吗？')) {
      deleteNote(noteId);
    }
  }, [deleteNote]);

  const handleCloseDialog = useCallback(() => {
    setShowNoteDialog(false);
    setEditingNote(null);
    setNoteTitle('');
    setNoteContent('');
  }, []);

  // 搜索功能
  const filteredNotes = useMemo(() => {
    if (!searchQuery.trim()) return state.notes;
    
    const query = searchQuery.toLowerCase();
    return state.notes.filter(note => {
      // 搜索标题
      if (note.title.toLowerCase().includes(query)) return true;
      
      // 搜索内容
      if (note.content.toLowerCase().includes(query)) return true;
      
      // 搜索创建时间
      const createdDate = note.createdAt.toLocaleDateString();
      const createdTime = note.createdAt.toLocaleString();
      if (createdDate.includes(query) || createdTime.includes(query)) return true;
      
      // 搜索更新时间
      const updatedDate = note.updatedAt.toLocaleDateString();
      const updatedTime = note.updatedAt.toLocaleString();
      if (updatedDate.includes(query) || updatedTime.includes(query)) return true;
      
      return false;
    });
  }, [state.notes, searchQuery]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const renderHome = () => (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          随手记
        </Typography>
        <Typography variant="body1" color="text.secondary">
          记录你的灵感和想法
        </Typography>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          统计信息
        </Typography>
        <Typography variant="body2" color="text.secondary">
          总笔记数: {state.notes.length}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          总字符数: {state.notes.reduce((total: number, note: Note) => total + note.content.length, 0)}
        </Typography>
      </Paper>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={handleNewNote}
          sx={{ minWidth: 200 }}
        >
          开始记录灵感
        </Button>
      </Box>
    </Container>
  );

  const renderNotes = () => (
    <Container maxWidth="md" sx={{ py: 2 }}>
      {state.notes.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            还没有笔记，点击下方按钮创建第一条笔记吧！
          </Typography>
        </Paper>
      ) : (
        <List>
          {state.notes.map((note: Note) => (
            <Paper key={note.id} elevation={1} sx={{ mb: 2 }}>
              <ListItem>
                <ListItemText
                  primary={note.title}
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {note.content}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        创建: {note.createdAt.toLocaleString()}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        更新: {note.updatedAt.toLocaleString()}
                      </Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleEditNote(note)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteNote(note.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}
    </Container>
  );

  const renderSearch = () => (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="搜索笔记标题、内容或时间..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {searchQuery && (
          <Box sx={{ mt: 1 }}>
            <Chip 
              label={`找到 ${filteredNotes.length} 条结果`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>
        )}
      </Paper>

      {filteredNotes.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {searchQuery ? '没有找到匹配的笔记' : '开始输入关键字搜索笔记'}
          </Typography>
        </Paper>
      ) : (
        <List>
          {filteredNotes.map((note: Note) => (
            <Paper key={note.id} elevation={1} sx={{ mb: 2 }}>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1">
                      {highlightText(note.title, searchQuery)}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {highlightText(note.content.substring(0, 100) + (note.content.length > 100 ? '...' : ''), searchQuery)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        创建: {note.createdAt.toLocaleString()}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        更新: {note.updatedAt.toLocaleString()}
                      </Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleEditNote(note)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteNote(note.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}
    </Container>
  );

  // 高亮搜索关键字
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>
          {part}
        </span>
      ) : part
    );
  };

  return (
    <Box sx={{ pb: 7 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            随手记
          </Typography>
        </Toolbar>
      </AppBar>

      {currentView === 'home' && renderHome()}
      {currentView === 'notes' && renderNotes()}
      {currentView === 'search' && renderSearch()}

      {/* 新建笔记浮动按钮 */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
        }}
        onClick={handleNewNote}
      >
        <AddIcon />
      </Fab>

      {/* 底部导航 */}
      <Paper
        sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}
        elevation={3}
      >
        <BottomNavigation
          value={currentView}
          onChange={(_, newValue) => setCurrentView(newValue)}
        >
          <BottomNavigationAction
            label="首页"
            value="home"
            icon={<HomeIcon />}
          />
          <BottomNavigationAction
            label="笔记"
            value="notes"
            icon={<NotesIcon />}
          />
          <BottomNavigationAction
            label="搜索"
            value="search"
            icon={<SearchIcon />}
          />
        </BottomNavigation>
      </Paper>

      {/* 笔记编辑对话框 */}
      <Dialog
        open={showNoteDialog}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingNote ? '编辑笔记' : '新建笔记'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="标题"
            fullWidth
            variant="outlined"
            value={noteTitle}
            onChange={(e) => setNoteTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="内容"
            fullWidth
            multiline
            rows={isMobile ? 10 : 6}
            variant="outlined"
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            取消
          </Button>
          <Button
            onClick={handleSaveNote}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!noteTitle.trim()}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <AppProvider>
        <NotificationProvider>
          <MainApp />
        </NotificationProvider>
      </AppProvider>
    </ThemeProvider>
  );
};

export default App;